import { Button, Progress } from 'antd';
import { useContext, useEffect, useRef, useState } from 'react';
import ImageContainer from '../General/ImageContainer';
import colorWheel from "../Theme/ColorWheel";
import { FormContext } from './FormEngine';
import FormReview from './FormReview';
import FormSection from './FormSection';
import Tabber, { allTabs } from './Tabber';

const styles = {
    column: {
        display: 'flex',
        flexDirection: 'column'
    },
    row: {
        display: 'flex',
        flexDirection: 'row'
    },
    leftColumn: {
        display: 'flex',
        flexDirection: 'column',
        minWidth: '300px',
        maxWidth: '300px'
    },
    rightColumn: {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1
    },
    blurb: {
        textAlign: 'left',
        fontStyle: 'italic',
        maxWidth: '250px',
        marginLeft: '15px',
        marginTop: '10px'
    },
    icon: {
        width: '14px',
        fontColor: 'grey',
        marginTop: '-3px',
        marginLeft: '-1px'
    },
    navButton: {
        minWidth: '185px',
        minHeight: '38px',
        backgroundColor: 'white',
        border: '1px solid lightgrey',
    }
};

const DigitalForm = ({ onSubmitForm, canSubmit }) => {
    const updateContext = (version) => {
        setUpdate(version);
    }

    const [, setUpdate] = useState(0);
    const [activeTab, setActiveTab] = useState('patient');
    const ctx = useContext(FormContext);

    useEffect(() => {
        async function initDigitalForm() {
            ctx?.initCallback(updateContext);
            await ctx?.initRules();


            const completed = ctx?.completedTabs();
            const activeTabs = allTabs.filter(t => !ctx?.disabledTabs?.includes(t.key));
            const incompleteTabs = allTabs.filter(t => !activeTabs?.includes(t.key) && !completed?.includes(t.key));
            const windTo = incompleteTabs?.length > 0 ? incompleteTabs[0] : activeTabs[0];

            setActiveTab(windTo?.key);
        }

        initDigitalForm();
    }, [ctx]);

    const sections = ctx?.layout.filter(section => section.tab === activeTab);
    const enabledTabs = allTabs.filter(t => !ctx?.disabledTabs?.includes(t.key));
    const reviewSubmitTabKey = 'review & submit'
    const scrollRef = useRef(null);

    const prevTabName = () => {
        var activeIndex = enabledTabs.findIndex(t => t.key === activeTab);
        if (activeIndex === 0) return null;
        else if (activeTab === reviewSubmitTabKey) return enabledTabs[enabledTabs.length - 1]?.text;
        else return enabledTabs[activeIndex - 1]?.text;
    }

    const onPrevTabClick = () => {
        if (activeTab === reviewSubmitTabKey && enabledTabs.length > 0) {
            setActiveTab(enabledTabs[enabledTabs.length - 1].key);
        }
        else {
            var activeIndex = enabledTabs.findIndex(t => t.key === activeTab);
            if (activeIndex !== 0) setActiveTab(enabledTabs[activeIndex - 1].key);
        }
        scrollRef.current.scrollIntoView();
    }

    const nextTabName = () => {
        var activeIndex = enabledTabs.findIndex(t => t.key === activeTab);
        if (activeIndex === enabledTabs.length - 1 && activeTab !== reviewSubmitTabKey) return 'Review & Submit';
        else return enabledTabs[activeIndex + 1]?.text;
    }

    const onNextTabClick = () => {
        var activeIndex = enabledTabs.findIndex(t => t.key === activeTab);
        if (activeIndex !== enabledTabs.length - 1) setActiveTab(enabledTabs[activeIndex + 1].key);
        else setActiveTab(reviewSubmitTabKey)
        scrollRef.current.scrollIntoView();
    }

    const nextTabDisabled = (ctx?.completedTabs().length !== 8 && enabledTabs.findIndex(t => t.key === activeTab) === enabledTabs.length - 1) ? true : false;

    const handleSubmitFormClick = () => {
        if (onSubmitForm) {
            onSubmitForm();
        }
    }

    return <div className='digitalform'>
        <div style={styles.column}>
            <div style={{ ...styles.row, paddingBottom: '10px' }}>
                <span ref={scrollRef}></span>
                <div style={styles.leftColumn}></div>
                <div style={styles.rightColumn} className='de-progress-wrapper'>
                    <Progress percent={Math.round(ctx?.percentComplete)} strokeColor={colorWheel.ivyGreen} />
                </div>
            </div>
            <div style={styles.row}>
                <div style={styles.leftColumn}>
                    <Tabber
                        activeTab={activeTab}
                        completed={ctx?.completedTabs()}
                        required={ctx?.requiredTabs()}
                        disabled={ctx?.disabledTabs}
                        onChange={(tabKey) => { setActiveTab(tabKey); scrollRef.current.scrollIntoView(); }}
                    />
                    <div style={styles.blurb} >
                        Editing fields only affects this application and will not update the underlying patient, provider, facility information in AssistPoint or within your Practice Management system/EHR
                    </div>
                </div>
                <div style={{ ...styles.rightColumn, marginTop: '-1em' }}>
                    {activeTab === "review & submit"
                        ? <FormReview />
                        : sections?.map(s =>
                            <FormSection
                                key={s.name}
                                name={s.name}
                                fields={s.fields}
                                onChange={ctx?.changeData.bind(ctx)}
                            />)
                    }
                    <div style={{ ...styles.row, justifyContent: 'space-between', paddingTop: '12px' }}>
                        {(activeTab !== 'patient' && prevTabName())
                            ? <Button
                                data-button='de-prev-tab'
                                className='de-form-nav-button'
                                style={styles.navButton}
                                onClick={() => onPrevTabClick()}
                            >
                                <ImageContainer iconName='arrow-left-long' styles={{ ...styles.icon, marginLeft: '-10px', marginRight: '20px' }} className='fontawesomeTextGrey' />
                                {prevTabName()}
                            </Button>
                            : <div />
                        }
                        {activeTab !== reviewSubmitTabKey
                            ? <Button
                                data-button='de-next-tab'
                                className='de-form-nav-button'
                                style={styles.navButton}
                                onClick={() => onNextTabClick()}
                                disabled={nextTabDisabled}
                            >
                                {nextTabName()}
                                <ImageContainer iconName='arrow-right-long' styles={{ ...styles.icon, marginLeft: '20px', marginRight: '-10px' }} className='fontawesomeTextGrey' />
                            </Button>
                            : <Button
                                data-button='de-submit'
                                style={{ minWidth: '76px', minHeight: '38px', color: 'white', backgroundColor: colorWheel.tangerineOrange, border: `1px solid ${colorWheel.tangerineOrange}` }}
                                onClick={() => handleSubmitFormClick()}
                                disabled={!canSubmit}
                            >
                                Submit
                            </Button>
                        }
                    </div>
                </div>
            </div>
        </div>
    </div >
}

export default DigitalForm;
